import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem, SetHorizonPayload} from '@taopix/taopix-core/dist/types/types';
import {
  Button,
  ContentPanel,
  FlipHorizontalIcon,
  FlipVerticalIcon,
  Rotate90Icon,
  RotatePictureRightIcon,
} from '@taopix/taopix-design-system';
import {RangeWithValue} from '@taopix/taopix-design-system';
import {
  AssetType,
  BoxType,
  RotationDirection,
  FlipImageProp,
  BoxLayer,
  NumBool,
} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type RotateAndFlipProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

type FlipExperienceValues = {
  rotate90: boolean;
  flipHorizontal: boolean;
  flipVertical: boolean;
  horizonAngle: boolean;
};

const RotateAndFlip = ({sectionKey, item}: RotateAndFlipProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {pictureBoxOptions, backgroundOptions, stickerOptions} = experience.EDITOR;

  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);

  const [horizonSliderValue, setHorizonSliderValue] = useState<number>(0);
  const [horizonActivated, setHorizonActivated] = useState<boolean>(false);

  const [notAvailableMessage, setNotAvailableMessage] = useState<string>('');

  const [rotateAndFlipOptions, setRotateAndFlipOptions] = useState<FlipExperienceValues>({
    rotate90: false,
    horizonAngle: true,
    flipHorizontal: true,
    flipVertical: true,
  });

  useEffect(() => {
    const normalizedHorizonAngle = app.core
      .get('utils')
      .normalizeHorizon(item?.imagehorizonangle as number);
    setHorizonSliderValue(normalizedHorizonAngle);
  }, [item?.imagehorizonangle]);

  const isActiveInExperience = (item: ComputedPageItem) => {
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      item?.type === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage
    ) {
      setRotateAndFlipOptions({
        rotate90: pictureBoxOptions.rotate90 && '' !== item.hirespath,
        horizonAngle: pictureBoxOptions.horizonAngle && '' !== item.hirespath,
        flipHorizontal: pictureBoxOptions.flipHorizontal && '' !== item.hirespath,
        flipVertical: pictureBoxOptions.flipVertical && '' !== item.hirespath,
      });
      return pictureBoxOptions.rotateAndFlip;
    }
    if (item.type === BoxType.Image && item.layer === BoxLayer.Background) {
      setRotateAndFlipOptions({
        rotate90: false, // no option
        horizonAngle: backgroundOptions.horizonAngle,
        flipHorizontal: backgroundOptions.flipHorizontal,
        flipVertical: backgroundOptions.flipVertical,
      });
      return backgroundOptions.rotateAndFlip;
    }
    if (AssetType.ScrapbookImage === item.hiresfiletype) {
      setRotateAndFlipOptions({
        rotate90: stickerOptions.rotate90,
        horizonAngle: false, // force off
        flipHorizontal: stickerOptions.flipHorizontal,
        flipVertical: stickerOptions.flipVertical,
      });
      return stickerOptions.rotateAndFlip;
    } else {
      return false;
    }
  };

  useEffect(() => {
    // If there is a selected item
    if (item && item.ref !== -1 && '' !== item.hirespath) {
      // Show or hide this section based on the relevant experience and advanced editing configuration
      setShowSection(isActiveInExperience(item));
      // Enable or disable this section based on the item's protection
      setDisableSection(
        NumBool.True === item?.protectcontents ||
          (item.errors?.imageMissing as boolean) ||
          ui.state.swapActive
      );
      // Set the message to 'not available' in case the section was hidden or disabled
      setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
    } else {
      // If no item is selected, hide the section button
      setShowSection(false);
      // And show the 'please select an item' message
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [
    item?.asset,
    item?.asset?.assetType,
    item?.type,
    item?.image,
    item?.shape,
    ui.state.swapActive,
  ]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'lg:flex-1',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'sm:max-w-full',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id={'rotateAndFlipPalette'}
      title={t('boxpalettes:str_TitleRotateAndFlip')}
      buttonIcon={<Rotate90Icon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      smallScreenPanelHeight={'auto'}
      subSection={true}
      disabled={disableSection}
      showButton={showSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses}>
        {!showSection || disableSection ? (
          // If the palette is not allowed by the experience, or is disabled by the box protection, show the relevant message
          // We show this message rather than closing/hiding the palette to avoid abrupt UI changes when no item or an invalid item is selected
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          <>
            {rotateAndFlipOptions.rotate90 && (
              <Button
                data-testid={'box-settings-rotate-90-button'}
                className={'w-full'}
                label={t('boxpalettes:str_LabelRotate90')}
                buttonStyle={'tertiary'}
                startIcon={<RotatePictureRightIcon />}
                labelAlignment={'left'}
                size={'large'}
                onClick={() => {
                  app.core
                    .dispatch('SHAPE.ROTATE_IMAGE', {direction: RotationDirection.ClockWise})
                    .catch(error => {
                      console.error(error);
                    });
                }}
              />
            )}
            {rotateAndFlipOptions.flipHorizontal && (
              <Button
                data-testid={'box-settings-flip-horizontal-button'}
                className={'w-full'}
                label={t('boxpalettes:str_LabelFlipHorizontal')}
                buttonStyle={'tertiary'}
                startIcon={<FlipHorizontalIcon />}
                labelAlignment={'left'}
                size={'large'}
                onClick={() => {
                  app.core
                    .dispatch('SHAPE.FLIP_IMAGE', FlipImageProp.FLIP_HORIZONTAL)
                    .catch(error => {
                      console.error(error);
                    });
                }}
              />
            )}
            {rotateAndFlipOptions.flipVertical && (
              <Button
                data-testid={'box-settings-flip-vertical-button'}
                className={'w-full'}
                label={t('boxpalettes:str_LabelFlipVertical')}
                buttonStyle={'tertiary'}
                startIcon={<FlipVerticalIcon />}
                labelAlignment={'left'}
                size={'large'}
                onClick={() => {
                  app.core
                    .dispatch('SHAPE.FLIP_IMAGE', FlipImageProp.FLIP_VERTICAL)
                    .catch(error => {
                      console.error(error);
                    });
                }}
              />
            )}
            {rotateAndFlipOptions.horizonAngle && (
              <ContentPanel>
                <RangeWithValue
                  data-testid={'box-settings-image-horizon-slider'}
                  key={`image-horizon-slider-${item?.ref}`}
                  value={horizonSliderValue}
                  min={-25.0}
                  max={25.0}
                  numberStep={0.1}
                  rangeStep={0.1}
                  id={'straighten-range'}
                  label={t('boxpalettes:str_TitleHorizonAngle')}
                  submitOnBlur={true}
                  onValueChange={(value: number, interacting?: boolean) => {
                    // dispatch SHOW.STRAIGHTEN to show lines
                    if (interacting && !horizonActivated) {
                      setHorizonActivated(false);
                      app.core
                        .dispatch('SHAPE.TOGGLE_STRAIGHTEN_GUIDES', {show: true})
                        .catch(error => {
                          console.error(error);
                        });
                    }
                    if (item?.ref !== -1) {
                      app.core
                        .dispatch(
                          'SHAPE.STRAIGHTEN_HORIZON',
                          {
                            type: 'set',
                            value: value,
                          } as SetHorizonPayload,
                          interacting
                        )
                        .catch(error => {
                          console.error(error);
                        });
                    }
                    // dispatch HIDE.STRAIGHTEN to hide lines
                    if (!interacting) {
                      setHorizonActivated(false);
                      app.core
                        .dispatch('SHAPE.TOGGLE_STRAIGHTEN_GUIDES', {show: false})
                        .catch(error => {
                          console.error(error);
                        });
                    }
                  }}
                  snapToValues={[0]}
                  showTickMarks
                  numberFieldSize={4}
                  isSmallScreen={ui.state.isSmallScreen}
                />
              </ContentPanel>
            )}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default RotateAndFlip;
